<template>
  <div class="lc-page">
    <!-- Top bar -->
    <div class="lc-topbar">
      <div class="lc-topbar-left">
        <h1 class="lc-title">Lifecycle</h1>
        <p class="lc-subtitle">Track and manage Contacts through every stage of your sales process</p>
      </div>
      <a href="#" class="lc-learn-link" target="_blank">Learn more ↗</a>
    </div>

    <!-- Feature toggle -->
    <div class="lc-feature-toggle">
      <div class="lc-toggle-text">
        <p class="lc-toggle-title">Show/Hide Lifecycle Stages</p>
        <p class="lc-toggle-desc">Display Lifecycle Stages in the Inbox and Contacts modules. Disabling this only hides the Stages. Your Contact data remains safe and unchanged.</p>
      </div>
      <label class="lc-switch">
        <input v-model="featureEnabled" type="checkbox" @change="onFeatureToggle" />
        <span class="lc-switch-slider"></span>
      </label>
    </div>

    <!-- Save/Cancel bar (shows when dirty) -->
    <div v-if="isDirty" class="lc-save-bar">
      <button class="lc-btn lc-btn--ghost" @click="cancelChanges">Cancel</button>
      <button class="lc-btn lc-btn--primary" :disabled="isSaving" @click="saveChanges">
        {{ isSaving ? 'Saving...' : 'Save' }}
      </button>
    </div>

    <!-- Two-column layout -->
    <div class="lc-sections">
      <!-- Lifecycle Stages -->
      <div class="lc-section">
        <div class="lc-section-header">
          <span class="lc-section-dot lc-dot--blue">●</span>
          <div>
            <h2 class="lc-section-title">Lifecycle Stages</h2>
            <p class="lc-section-desc">Organize and track Contacts through key stages of their journey. A default inbox will be created in the Inbox module.</p>
          </div>
        </div>

        <div
          class="lc-stage-list"
          @dragover.prevent
          @drop="onDrop($event, 'lifecycle')"
        >
          <div
            v-for="(stage, idx) in lifecycleList"
            :key="stage._key"
            class="lc-stage-card"
            draggable="true"
            @dragstart="onDragStart($event, 'lifecycle', idx)"
            @dragend="onDragEnd"
            @dragover.prevent="onDragOver($event, 'lifecycle', idx)"
          >
            <!-- Drag handle: 6 dots 2×3 -->
            <span class="lc-drag-handle" draggable="false">
              <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
                <circle cx="2.5" cy="2.5" r="1.5"/>
                <circle cx="7.5" cy="2.5" r="1.5"/>
                <circle cx="2.5" cy="8" r="1.5"/>
                <circle cx="7.5" cy="8" r="1.5"/>
                <circle cx="2.5" cy="13.5" r="1.5"/>
                <circle cx="7.5" cy="13.5" r="1.5"/>
              </svg>
            </span>

            <!-- Emoji picker -->
            <div class="lc-emoji-wrap">
              <button class="lc-emoji-btn" @click.stop="toggleEmojiPicker(stage)">
                {{ stage.emoji || '🔵' }}
              </button>
              <div v-if="stage._emojiOpen" class="lc-emoji-picker-outer" @click.stop>
                <EmojiInput :on-click="(e) => setEmoji(stage, e)" />
              </div>
            </div>

            <div class="lc-stage-body">
              <!-- Stage number label -->
              <span class="lc-stage-num">Stage {{ idx + 1 }}</span>
              <input
                v-model="stage.name"
                type="text"
                class="lc-stage-name-input"
                placeholder="Stage name"
                @input="markDirty"
              />
              <button
                class="lc-show-desc-btn"
                @click="stage._showDesc = !stage._showDesc"
              >
                {{ stage._showDesc ? 'Hide description' : 'Show description' }}
              </button>
              <textarea
                v-if="stage._showDesc"
                v-model="stage.description"
                class="lc-stage-desc-input"
                placeholder="Description..."
                rows="2"
                @input="markDirty"
              ></textarea>
            </div>

            <!-- Badges + actions -->
            <div class="lc-stage-right">
              <span v-if="stage.is_default" class="lc-badge lc-badge--default">Default</span>
              <div class="lc-stage-menu-wrap">
                <button class="lc-icon-btn" @click.stop="toggleMenu(stage)">⋯</button>
                <div v-if="stage._menuOpen" class="lc-stage-menu" @click.stop>
                  <button @click="setDefault(stage); stage._menuOpen = false">
                    <svg class="lc-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    Set as default
                  </button>
                  <button
                    class="lc-danger"
                    :class="{ 'lc-disabled': stage.is_default }"
                    :disabled="stage.is_default"
                    :title="stage.is_default ? 'Set another stage as default before deleting this one' : ''"
                    @click="!stage.is_default && (removeStage('lifecycle', idx), stage._menuOpen = false)"
                  >
                    <svg class="lc-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button class="lc-add-btn" @click="addStage('lifecycle')">+ Add Stage</button>
      </div>

      <!-- Lost Stages -->
      <div class="lc-section lc-section--lost">
        <div class="lc-section-header">
          <span class="lc-section-dot lc-dot--orange">●</span>
          <div>
            <h2 class="lc-section-title">Lost Stages</h2>
            <p class="lc-section-desc">Track where Contacts go after exiting your sales process. No default inbox will be created in the Inbox module for Lost Stages.</p>
          </div>
        </div>

        <div
          class="lc-stage-list"
          @dragover.prevent
          @drop="onDrop($event, 'lost')"
        >
          <div
            v-for="(stage, idx) in lostList"
            :key="stage._key"
            class="lc-stage-card"
            draggable="true"
            @dragstart="onDragStart($event, 'lost', idx)"
            @dragend="onDragEnd"
            @dragover.prevent="onDragOver($event, 'lost', idx)"
          >
            <!-- Drag handle: 6 dots 2×3 -->
            <span class="lc-drag-handle" draggable="false">
              <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
                <circle cx="2.5" cy="2.5" r="1.5"/>
                <circle cx="7.5" cy="2.5" r="1.5"/>
                <circle cx="2.5" cy="8" r="1.5"/>
                <circle cx="7.5" cy="8" r="1.5"/>
                <circle cx="2.5" cy="13.5" r="1.5"/>
                <circle cx="7.5" cy="13.5" r="1.5"/>
              </svg>
            </span>

            <div class="lc-emoji-wrap">
              <button class="lc-emoji-btn" @click.stop="toggleEmojiPicker(stage)">
                {{ stage.emoji || '🔴' }}
              </button>
              <div v-if="stage._emojiOpen" class="lc-emoji-picker-outer" @click.stop>
                <EmojiInput :on-click="(e) => setEmoji(stage, e)" />
              </div>
            </div>

            <div class="lc-stage-body">
              <span class="lc-stage-num">Stage {{ idx + 1 }}</span>
              <input
                v-model="stage.name"
                type="text"
                class="lc-stage-name-input"
                placeholder="Stage name"
                @input="markDirty"
              />
              <button
                class="lc-show-desc-btn"
                @click="stage._showDesc = !stage._showDesc"
              >
                {{ stage._showDesc ? 'Hide description' : 'Show description' }}
              </button>
              <textarea
                v-if="stage._showDesc"
                v-model="stage.description"
                class="lc-stage-desc-input"
                placeholder="Description..."
                rows="2"
                @input="markDirty"
              ></textarea>
            </div>

            <div class="lc-stage-right">
              <div class="lc-stage-menu-wrap">
                <button class="lc-icon-btn" @click.stop="toggleMenu(stage)">⋯</button>
                <div v-if="stage._menuOpen" class="lc-stage-menu" @click.stop>
                  <button class="lc-danger" @click="removeStage('lost', idx); stage._menuOpen = false">
                    <svg class="lc-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button class="lc-add-btn" @click="addStage('lost')">+ Add Stage</button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';

const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

let keyCounter = 0;

function makeLocalStage(s) {
  keyCounter += 1;
  return {
    ...s,
    _key: `stage_${keyCounter}`,
    _menuOpen: false,
    _emojiOpen: false,
    _showDesc: !!s.description,
    _isNew: !s.id,
  };
}

export default {
  name: 'LifecycleSettings',
  components: { EmojiInput },

  data() {
    return {
      featureEnabled: false,
      isDirty: false,
      isSaving: false,
      lifecycleList: [],
      lostList: [],
      // drag state
      _dragType: null,
      _dragIdx: null,
    };
  },

  computed: {
    storeStages() {
      const stages =
        this.$store.getters['lifecycleStages/getAllLifecycleStages'];
      return Array.isArray(stages) ? stages : [];
    },
    accountId() {
      return Number(this.$route.params.accountId);
    },
  },

  watch: {
    storeStages: {
      immediate: true,
      handler(stages) {
        if (!this.isDirty) {
          this.loadFromStore(stages);
        }
      },
    },
  },

  mounted() {
    this.$store.dispatch('lifecycleStages/get');
    document.addEventListener('click', this.onOutsideClick);
    // Load the persisted toggle value from account settings
    this.featureEnabled =
      this.$store.getters['accounts/isLifecycleStageSelectorEnabled'](
        this.accountId
      );
  },

  beforeUnmount() {
    document.removeEventListener('click', this.onOutsideClick);
  },

  methods: {
    loadFromStore(stages) {
      const lifecycle = (stages || [])
        .filter(s => s.stage_type === 'lifecycle')
        .sort((a, b) => a.position - b.position)
        .map(makeLocalStage);
      const lost = (stages || [])
        .filter(s => s.stage_type === 'lost')
        .sort((a, b) => a.position - b.position)
        .map(makeLocalStage);
      this.lifecycleList = lifecycle;
      this.lostList = lost;
    },

    markDirty() {
      this.isDirty = true;
    },

    async onFeatureToggle() {
      try {
        await this.$store.dispatch('accounts/update', {
          show_lifecycle_stages: this.featureEnabled,
          options: { silent: true },
        });
      } catch (e) {
        // Revert toggle on failure
        this.featureEnabled = !this.featureEnabled;
      }
    },

    cancelChanges() {
      this.isDirty = false;
      this.loadFromStore(this.storeStages);
    },

    async saveChanges() {
      this.isSaving = true;
      try {
        // Snapshot store stages BEFORE starting creates/updates so the deletion
        // loop doesn't accidentally delete stages that were just created.
        const originalStoreStages = Array.isArray(this.storeStages)
          ? [...this.storeStages]
          : [];

        const allStages = [
          ...this.lifecycleList.map((s, i) => ({ ...s, stage_type: 'lifecycle', position: i })),
          ...this.lostList.map((s, i) => ({ ...s, stage_type: 'lost', position: i })),
        ];

        for (const stage of allStages) {
          const payload = {
            name: stage.name,
            description: stage.description,
            emoji: stage.emoji,
            position: stage.position,
            stage_type: stage.stage_type,
            is_default: stage.is_default || false,
          };
          if (stage.id) {
            await this.$store.dispatch('lifecycleStages/update', { id: stage.id, ...payload });
          } else {
            await this.$store.dispatch('lifecycleStages/create', payload);
          }
        }

        // Delete stages that existed BEFORE this save and are no longer in the local lists.
        // Use originalStoreStages (snapshot) — NOT this.storeStages — to avoid deleting
        // stages that were just created during this loop.
        const localIds = new Set(allStages.filter(s => s.id).map(s => s.id));
        for (const storeStage of originalStoreStages) {
          if (!localIds.has(storeStage.id)) {
            await this.$store.dispatch('lifecycleStages/delete', storeStage.id);
          }
        }

        this.isDirty = false;
        // Reload from store to reflect any newly assigned IDs
        await this.$store.dispatch('lifecycleStages/get');
        this.loadFromStore(this.storeStages);
      } catch (e) {
        alert('Error saving: ' + e.message);
      } finally {
        this.isSaving = false;
      }
    },

    addStage(type) {
      const list = type === 'lifecycle' ? this.lifecycleList : this.lostList;
      const newStage = makeLocalStage({
        name: '',
        description: '',
        emoji: type === 'lifecycle' ? '🔵' : '🔴',
        stage_type: type,
        is_default: false,
      });
      list.push(newStage);
      this.markDirty();
    },

    removeStage(type, idx) {
      const list = type === 'lifecycle' ? this.lifecycleList : this.lostList;
      if (type === 'lifecycle' && list[idx]?.is_default) return; // guard
      list.splice(idx, 1);
      this.markDirty();
    },

    async setDefault(stage) {
      // Update all lifecycle stages: only the chosen one gets is_default=true.
      // Persist immediately to the API without requiring a manual Save click.
      const list = this.lifecycleList;
      list.forEach(s => { s.is_default = false; });
      stage.is_default = true;
      stage._menuOpen = false;

      try {
        // Persist all lifecycle stages that have an id
        for (const s of list) {
          if (s.id) {
            await this.$store.dispatch('lifecycleStages/update', {
              id: s.id,
              name: s.name,
              description: s.description,
              emoji: s.emoji,
              position: s.position,
              stage_type: s.stage_type,
              is_default: s.is_default,
            });
          }
        }
        // Re-sync local state from store to pick up server-side changes
        this.loadFromStore(this.storeStages);
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert('Error setting default: ' + e.message);
      }
    },

    toggleMenu(stage) {
      // close others first
      this.closeAllMenus();
      stage._menuOpen = !stage._menuOpen;
    },

    toggleEmojiPicker(stage) {
      const was = stage._emojiOpen;
      this.closeAllPickers();
      stage._emojiOpen = !was;
    },

    setEmoji(stage, emoji) {
      stage.emoji = emoji;
      stage._emojiOpen = false;
      this.markDirty();
    },

    onOutsideClick() {
      this.closeAllMenus();
      this.closeAllPickers();
    },

    closeAllMenus() {
      [this.lifecycleList, this.lostList].forEach(list => {
        list.forEach(s => { s._menuOpen = false; });
      });
    },

    closeAllPickers() {
      [this.lifecycleList, this.lostList].forEach(list => {
        list.forEach(s => { s._emojiOpen = false; });
      });
    },

    // Drag and drop
    onDragStart(event, type, idx) {
      this._dragType = type;
      this._dragIdx = idx;
      event.dataTransfer.effectAllowed = 'move';
    },

    onDragEnd() {
      this._dragType = null;
      this._dragIdx = null;
    },

    onDragOver(event, type, idx) {
      if (this._dragType !== type) return;
      event.preventDefault();
    },

    onDrop(event, type) {
      if (this._dragType !== type || this._dragIdx === null) return;
      const list = type === 'lifecycle' ? this.lifecycleList : this.lostList;
      const target = this.getDropIndex(event, type);
      if (target === null || target === this._dragIdx) return;

      const [item] = list.splice(this._dragIdx, 1);
      list.splice(target, 0, item);
      this._dragIdx = null;
      this.markDirty();
    },

    getDropIndex(event, type) {
      const listEl = event.currentTarget;
      const cards = listEl.querySelectorAll('.lc-stage-card');
      const mouseY = event.clientY;
      for (let i = 0; i < cards.length; i++) {
        const rect = cards[i].getBoundingClientRect();
        if (mouseY < rect.top + rect.height / 2) return i;
      }
      const list = type === 'lifecycle' ? this.lifecycleList : this.lostList;
      return list.length - 1;
    },
  },
};
</script>

<style scoped>
.lc-page {
  width: 100%;
  font-family: 'Inter', sans-serif;
  color: rgb(var(--slate-12));
}

/* Top bar */
.lc-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.lc-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: rgb(var(--slate-12));
  margin: 0 0 0.25rem;
}

.lc-subtitle {
  color: rgb(var(--slate-10));
  font-size: 0.875rem;
  margin: 0;
}

.lc-learn-link {
  font-size: 0.8125rem;
  color: rgb(var(--blue-9));
  text-decoration: none;
  white-space: nowrap;
}

.lc-learn-link:hover { text-decoration: underline; }

/* Feature toggle */
.lc-feature-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgb(var(--slate-3));
  border: 1px solid rgb(var(--slate-5));
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
}

.lc-toggle-title {
  font-weight: 600;
  font-size: 0.9375rem;
  color: rgb(var(--slate-12));
  margin: 0 0 0.25rem;
}

.lc-toggle-desc {
  font-size: 0.8125rem;
  color: rgb(var(--slate-10));
  margin: 0;
  max-width: 640px;
}

/* Switch */
.lc-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.lc-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.lc-switch-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: rgb(var(--slate-6));
  border-radius: 24px;
  transition: background 0.2s;
}

.lc-switch-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
}

.lc-switch input:checked + .lc-switch-slider { background: #1f93ff; }
.lc-switch input:checked + .lc-switch-slider::before { transform: translateX(20px); }

/* Save bar */
.lc-save-bar {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: rgb(var(--slate-3));
  border: 1px solid rgb(var(--slate-5));
  border-radius: 8px;
}

/* Buttons */
.lc-btn {
  padding: 0.45rem 1.125rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.2s;
}

.lc-btn--primary {
  background: #1f93ff;
  color: #fff;
}

.lc-btn--primary:hover { background: #0078d4; }
.lc-btn--primary:disabled { opacity: 0.5; cursor: default; }

.lc-btn--ghost {
  background: transparent;
  border: 1px solid rgb(var(--slate-6));
  color: rgb(var(--slate-10));
}

.lc-btn--ghost:hover { background: rgb(var(--slate-4)); }

/* Sections layout */
.lc-sections {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

/* Single section */
.lc-section {
  background: transparent;
}

.lc-section--lost .lc-section-header { border-left-color: #f97316; }

.lc-section-header {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1rem 1.25rem;
  background: rgb(var(--slate-3));
  border-left: 3px solid #1f93ff;
  border-radius: 8px 8px 0 0;
  border: 1px solid rgb(var(--slate-5));
  border-bottom: none;
}

.lc-section--lost .lc-section-header { border-left: 3px solid #f97316; background: rgb(var(--amber-2)); }

.lc-section-dot {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 1px;
}

.lc-dot--blue { color: rgb(var(--blue-9)); }
.lc-dot--orange { color: #f97316; }

.lc-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: rgb(var(--slate-12));
  margin: 0 0 0.25rem;
}

.lc-section-desc {
  font-size: 0.8125rem;
  color: rgb(var(--slate-10));
  margin: 0;
  line-height: 1.5;
}

/* Stage list */
.lc-stage-list {
  display: flex;
  flex-direction: column;
  border: 1px solid rgb(var(--slate-5));
  border-top: none;
  min-height: 40px;
}

/* Stage card — everything baseline-aligned to the input row */
.lc-stage-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgb(var(--slate-1));
  border-bottom: 1px solid rgb(var(--slate-5));
  cursor: grab;
  position: relative;
}

.lc-stage-card:last-child { border-bottom: none; }
.lc-stage-card:hover { background: rgb(var(--slate-2)); }

.lc-drag-handle {
  color: rgb(var(--slate-8));
  cursor: grab;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  align-self: center;
  line-height: 0;
}

/* Emoji */
.lc-emoji-wrap {
  position: relative;
  flex-shrink: 0;
}

.lc-emoji-btn {
  background: rgb(var(--slate-2));
  border: 1px solid rgb(var(--slate-6));
  border-radius: 6px;
  width: 34px;
  height: 34px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s;
}

.lc-emoji-btn:hover { border-color: #1f93ff; }

.lc-emoji-picker-outer {
  position: absolute;
  top: 40px;
  left: 0;
  z-index: 200;
}

/* Stage body */
.lc-stage-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.lc-stage-num {
  font-size: 0.75rem;
  color: rgb(var(--slate-8));
  font-weight: 500;
}

.lc-stage-name-input {
  background: transparent;
  border: none;
  border-bottom: 1px solid rgb(var(--slate-6));
  color: rgb(var(--slate-12));
  font-size: 0.9375rem;
  font-weight: 600;
  padding: 0.125rem 0;
  outline: none;
  width: 100%;
  transition: border-color 0.2s;
}

.lc-stage-name-input:focus { border-bottom-color: #1f93ff; }
.lc-stage-name-input::placeholder { color: rgb(var(--slate-8)); }

.lc-show-desc-btn {
  background: none;
  border: none;
  color: rgb(var(--slate-9));
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
  text-align: left;
  transition: color 0.2s;
}

.lc-show-desc-btn:hover { color: rgb(var(--slate-10)); }

.lc-stage-desc-input {
  width: 100%;
  background: rgb(var(--slate-2));
  border: 1px solid rgb(var(--slate-6));
  border-radius: 5px;
  color: rgb(var(--slate-10));
  font-size: 0.8125rem;
  padding: 0.375rem 0.5rem;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.lc-stage-desc-input:focus { border-color: #1f93ff; }

/* Stage right actions */
.lc-stage-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  align-self: center;
}

.lc-badge {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  white-space: nowrap;
}

.lc-badge--default {
  background: rgb(var(--slate-5));
  color: rgb(var(--blue-9));
}

.lc-badge--new {
  background: rgb(var(--teal-2));
  color: rgb(var(--teal-9));
}

.lc-stage-menu-wrap {
  position: relative;
}

.lc-icon-btn {
  background: none;
  border: none;
  color: rgb(var(--slate-8));
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 1.125rem;
  transition: color 0.2s, background 0.2s;
}

.lc-icon-btn:hover {
  color: rgb(var(--slate-10));
  background: rgb(var(--slate-4));
}

.lc-stage-menu {
  position: absolute;
  right: 0;
  top: 28px;
  z-index: 100;
  background: rgb(var(--slate-2));
  border: 1px solid rgb(var(--slate-5));
  border-radius: 6px;
  min-width: 150px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  overflow: hidden;
}

.lc-stage-menu button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  background: none;
  border: none;
  color: rgb(var(--slate-12));
  font-size: 0.8125rem;
  padding: 0.6rem 1rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

.lc-menu-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.lc-stage-menu button:hover { background: rgb(var(--slate-4)); }
.lc-stage-menu button.lc-danger { color: rgb(var(--ruby-9)); }
.lc-stage-menu button.lc-danger:hover { background: rgb(var(--ruby-2)); }
.lc-stage-menu button.lc-disabled,
.lc-stage-menu button[disabled] {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

/* Add stage button */
.lc-add-btn {
  width: 100%;
  padding: 0.75rem;
  background: rgb(var(--slate-1));
  border: 1px solid rgb(var(--slate-5));
  border-top: none;
  border-radius: 0 0 8px 8px;
  color: rgb(var(--blue-9));
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  transition: background 0.2s;
}

.lc-add-btn:hover { background: rgb(var(--slate-2)); }

/* Stage body: stretch to full height but keep text top-to-bottom */
.lc-stage-body {
  align-self: center;
}
</style>

<!-- Override EmojiInput's built-in absolute positioning so it flows inside our wrapper -->
<style>
.lc-emoji-picker-outer .emoji-dialog {
  position: relative !important;
  top: auto !important;
  right: auto !important;
}
</style>
